/**
 * CORE-1743 — «Финансы → Мои документы»: что показать по состоянию пакета
 * мастера (CORE-1742). Без React Native — правила проверяются под jest.
 *
 * Всё, что здесь решается, — только подача: подписан ли документ и оформлен ли
 * мастер, говорит сервер. Клиент не отмечает подпись сам и роль по своему
 * состоянию не выдаёт.
 */
import type { MasterOnboardingDocument, MasterOnboardingState, MasterTaxState } from '../../types/masterOnboarding';

/** Постоянный адрес раздела — его же ставит в уведомление сервер (CORE-1742). */
export const MASTER_DOCUMENTS_PATH = '/finance/documents';

/**
 * Договор подписывают, согласие — дают. Это разные юридические действия, и
 * кнопка у них разная (CORE-1563). Формулировки рабочие: окончательные тексты
 * действий и дисклеймеров придут из CORE-1563.
 */
export type MasterDocumentKind = 'agreement' | 'consent';

interface DocumentMeta {
  title: string;
  kind: MasterDocumentKind;
}

const DOCUMENTS: Record<string, DocumentMeta> = {
  'master-platform-agreement': { title: 'Договор мастера с платформой', kind: 'agreement' },
  'master-agency-agreement': { title: 'Агентский договор', kind: 'agreement' },
  'master-pd-distribution-consent': { title: 'Согласие на распространение персональных данных', kind: 'consent' },
};

export function documentMeta(documentId: string): DocumentMeta {
  return DOCUMENTS[documentId]
    // Документ, которого клиент не знает, всё равно показывается и
    // подписывается: состав пакета решает сервер.
    ?? { title: documentId, kind: documentId.includes('consent') ? 'consent' : 'agreement' };
}

export interface SignAction {
  button: string;
  disclaimer: string;
}

export function signAction(kind: MasterDocumentKind): SignAction {
  return kind === 'consent'
    ? {
      button: 'Дать согласие',
      disclaimer: 'Нажимая «Дать согласие», вы даёте согласие на условиях этого документа в показанной редакции.',
    }
    : {
      button: 'Подписать',
      disclaimer: 'Нажимая «Подписать», вы подтверждаете, что прочитали договор, и заключаете его в показанной редакции.',
    };
}

export type MasterDocumentsView =
  /** Пакета нет: заявка не одобрена или мастер оформлен без пакета. */
  | 'empty'
  /** Пакет открыт, ни один документ не подписан. */
  | 'awaiting'
  /** Подписана часть. */
  | 'partial'
  /** Все подписаны, а роль сервер ещё не подтвердил. */
  | 'activating'
  /** Оформление завершено. */
  | 'complete';

export function documentsView(state: MasterOnboardingState): MasterDocumentsView {
  const docs = state.documents ?? [];
  if (docs.length === 0) return 'empty';
  const signed = docs.filter((d) => d.status === 'signed').length;
  if (signed === docs.length) return state.status === 'active' ? 'complete' : 'activating';
  return signed === 0 ? 'awaiting' : 'partial';
}

export function signedCount(state: MasterOnboardingState): number {
  return (state.documents ?? []).filter((d) => d.status === 'signed').length;
}

/** Строка блока на «Финансах» и подзаголовок раздела. */
export function documentsSummary(state: MasterOnboardingState): string {
  const total = state.documents?.length ?? 0;
  if (signingLocked(state)) return 'Перед подписанием — проверка налогового статуса';
  switch (documentsView(state)) {
    case 'empty':
      return 'Документов пока нет';
    case 'complete':
      return 'Все документы подписаны';
    case 'activating':
      return 'Все документы подписаны, завершаем оформление';
    default:
      return `Подписано ${signedCount(state)} из ${total}`;
  }
}

export function statusLabel(doc: MasterOnboardingDocument): string {
  return doc.status === 'signed' ? 'Подписан' : 'Нужно подписать';
}

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

/** `2026-10-01` → «1 октября 2026». Дата редакции — календарная, без часового пояса. */
export function formatEditionDate(value: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!m) return null;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month} ${m[1]}` : null;
}

/** Время подписи сервера — в часовом поясе человека: «19 октября 2026, 12:05». */
export function formatSignedAt(value: string | null | undefined): string | null {
  if (!value) return null;
  // Сервер отдаёт UTC без суффикса — без `Z` строка прочиталась бы как местное время.
  const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function editionLine(doc: MasterOnboardingDocument): string {
  const date = formatEditionDate(doc.effectiveFrom);
  return date ? `Редакция ${doc.version} от ${date}` : `Редакция ${doc.version}`;
}

/** Ошибка подписи — словами; успехом она не показывается никогда. */
export function signErrorMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  switch (code) {
    case 'master_document_edition_mismatch':
      return 'Редакция документа изменилась. Обновите страницу и прочитайте актуальный текст.';
    case 'master_package_not_found':
    case 'master_document_not_in_package':
      return 'Этого документа нет в вашем пакете. Обновите страницу.';
    case 'master_tax_check_required':
      return 'Подписать документ можно после налоговой проверки. Обновите страницу.';
    default:
      return 'Не удалось подписать документ. Попробуйте ещё раз.';
  }
}

// ── CORE-1743: налоговая проверка перед подписанием (CORE-1742) ─────────────

const TAX_STAGES = new Set(['tax_data_required', 'tax_status_required', 'manual_review', 'manual_review_rejected']);

/**
 * Закрыто ли подписание налоговой проверкой. Решает сервер (`stage`); старый
 * сервер без этапа подписание не закрывает — как было до налоговой проверки.
 */
export function signingLocked(state: MasterOnboardingState | null): boolean {
  return !!state?.stage && TAX_STAGES.has(state.stage);
}

export type TaxStepView =
  /** Статуса и ИНН ещё нет или проверку не запускали. */
  | 'data_required'
  /** Проверку запустили, а данных в контуре не хватило. */
  | 'data_missing'
  /** CORE-1924 — обычное физлицо мастером не становится: нужен НПД или ИП. */
  | 'status_required'
  | 'manual_review'
  | 'manual_rejected'
  /** Пройдена — можно подписывать. */
  | 'cleared';

/** Что показать в шаге налоговой проверки; null — шага нет. */
export function taxStepView(state: MasterOnboardingState | null): TaxStepView | null {
  if (!state?.stage || !state.tax || state.status !== 'awaiting_signature') return null;
  switch (state.stage) {
    case 'tax_data_required':
      return state.tax.attempt > 0 && state.tax.fnsResult === 'data_missing' ? 'data_missing' : 'data_required';
    case 'tax_status_required':
      return 'status_required';
    case 'manual_review':
      return 'manual_review';
    case 'manual_review_rejected':
      return 'manual_rejected';
    case 'awaiting_signature':
      return 'cleared';
    default:
      return null;
  }
}

/** Можно ли запустить проверку из этого состояния (повтор во время ручной — нет). */
export function canRunTaxCheck(view: TaxStepView | null): boolean {
  return view === 'data_required' || view === 'data_missing' || view === 'status_required' || view === 'manual_rejected';
}

const TAX_STATUS_LABELS: Record<string, string> = {
  self_employed: 'Самозанятый (НПД)',
  sole_trader: 'Индивидуальный предприниматель',
  individual: 'Физическое лицо',
};

export function taxStatusLabel(taxStatus: string | null | undefined): string | null {
  return (taxStatus && TAX_STATUS_LABELS[taxStatus]) || null;
}

export interface TaxStepCopy {
  title: string;
  body: string;
}

const MANUAL_REASON_TEXT: Record<string, string> = {
  npd_not_confirmed: 'ФНС не подтвердила статус самозанятого на сегодняшнюю дату. Мы проверим данные вручную и напишем, когда закончим.',
  fns_unavailable: 'ФНС сейчас недоступна, поэтому проверим данные вручную. Мы напишем, когда закончим.',
  other_status: 'Для вашего налогового статуса автоматической проверки пока нет, проверим вручную. Мы напишем, когда закончим.',
};

/** Тексты шага — по этапу и причине с сервера. */
export function taxStepCopy(view: TaxStepView, tax: MasterTaxState | null | undefined): TaxStepCopy {
  switch (view) {
    case 'data_required':
      return {
        title: 'Налоговый статус',
        body: 'Прежде чем подписывать документы, укажите налоговый статус и ИНН в реквизитах и запустите проверку. Статус самозанятого мы сверим с ФНС.',
      };
    case 'data_missing':
      return {
        title: 'Не хватает данных для проверки',
        body: 'Укажите налоговый статус и ИНН в реквизитах, затем запустите проверку снова.',
      };
    case 'status_required':
      return {
        title: 'Нужен статус самозанятого или ИП',
        body: 'Стать мастером можно как самозанятый или индивидуальный предприниматель. Оформите самозанятость или ИП, укажите новый статус в реквизитах и пройдите проверку снова. Одобрение заявки остаётся в силе.',
      };
    case 'manual_review': {
      const reason = (tax?.manualReviewReason && MANUAL_REASON_TEXT[tax.manualReviewReason])
        ?? 'Мы проверим налоговые данные вручную и напишем, когда закончим.';
      const contact = tax?.contactForQuestions?.trim();
      return {
        title: 'Проверяем налоговые данные вручную',
        body: contact ? `${reason} По вопросам — ${contact}.` : reason,
      };
    }
    case 'manual_rejected': {
      const comment = tax?.manualOutcomeComment?.trim();
      const base = 'Проверьте налоговый статус и ИНН в реквизитах, при необходимости исправьте их и запустите проверку снова. Одобрение заявки остаётся в силе.';
      return {
        title: 'Налоговый статус не подтвердился',
        body: comment ? `${base} Комментарий проверяющего: ${comment}` : base,
      };
    }
    case 'cleared':
      return { title: 'Налоговый статус проверен', body: 'Можно подписывать документы.' };
  }
}

/** Ответ ФНС отдельной строкой — ручное решение его не меняет. */
export function fnsAnswerLine(tax: MasterTaxState | null | undefined): string | null {
  const message = tax?.fnsMessage?.trim();
  return message ? `Ответ ФНС: ${message}` : null;
}

/** Ошибка запуска проверки — словами; состояние после неё перечитывается с сервера. */
export function taxCheckErrorMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  return code === 'master_onboarding_not_open'
    ? 'Пакет документов уже подписан или ещё не открыт. Обновите страницу.'
    : 'Не удалось запустить проверку. Попробуйте ещё раз.';
}

/** Плашка над документами после одобрения. */
export function approvalNotice(state: MasterOnboardingState): string {
  return signingLocked(state)
    ? 'Ваша заявка на статус мастера одобрена. Сначала проверим налоговый статус, затем вы подпишете документы ниже.'
    : 'Ваша заявка на статус мастера одобрена. Статус мастера станет доступен после подписания всех документов ниже.';
}

/** Блок на «Финансах» нужен, когда есть пакет: до оформления и после. */
export function showDocumentsBlock(state: MasterOnboardingState | null): boolean {
  return !!state && ((state.documents?.length ?? 0) > 0 || state.status === 'awaiting_signature');
}

/** Абзацы текста редакции: пустая строка делит абзацы, `## ` — заголовок. */
export type EditionBlock = { kind: 'heading' | 'paragraph'; text: string };

export function editionBlocks(text: string): EditionBlock[] {
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .map((chunk) => (/^#{1,6}\s/.test(chunk)
      ? { kind: 'heading' as const, text: chunk.replace(/^#{1,6}\s+/, '') }
      : { kind: 'paragraph' as const, text: chunk }));
}
